import React, { useState, useEffect } from 'react';
import EmployeeApi from './employeeApi';
import { Employee } from './employeeType';
import { fetchEmployeeType, fetchEmployeeTitle } from '../reference/referenceApi';
import { useLocation, useNavigate } from 'react-router-dom';
import HorizontalMenu from '../components/common/horizontalMenu';
import Breadcrumbs from '../components/common/breadcrumbs';
import { validateEmail, validateAndFormatPhoneNumber } from '../lib/validator';
import { Save } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select } from '../components/ui/select';
import { Alert } from '../components/ui/alert';

const UpdateEmployeePage: React.FC = () => {
  const breadcrumbs = [
    { label: 'Company', path: '/main/company' },
    { label: 'Employee', path: '/main/company/employee' },
    { label: 'Update', path: '/main/company/employee/update' },
  ];

  const location = useLocation();
  const employeeData = location.state?.employee as Employee;
  const navigate = useNavigate();

  const [employee, setEmployee] = useState<Partial<Employee>>(employeeData || {});
  const [employeeTypeOptions, setEmployeeTypeOptions] = useState<string[]>([]);
  const [employeeTitleOptions, setEmployeeTitleOptions] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  // Load employee type options
  useEffect(() => {
    const loadEmployeeType = async () => {
      try {
        const employeeTypeData = await fetchEmployeeType();
        setEmployeeTypeOptions(employeeTypeData);
      } catch (error) {
        if (error instanceof Error) {
          setErrorMessage(error.message);
        } else {
          setErrorMessage('An unknown error occurred.');
        }
      }
    };
    loadEmployeeType();
  }, []);

  // Load employee title options
  useEffect(() => {
    const loadEmployeeTitle = async () => {
      try {
        const employeeTitleData = await fetchEmployeeTitle();
        setEmployeeTitleOptions(employeeTitleData);
      } catch (error) {
        if (error instanceof Error) {
          setErrorMessage(error.message);
        } else {
          setErrorMessage('An unknown error occurred.');
        }
      }
    };
    loadEmployeeTitle();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setEmployee((prev) => ({ ...prev, [name]: value }));
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let errors: string[] = [];

    switch (name) {
      case 'phone':
        const phoneResult = validateAndFormatPhoneNumber(value);
        if (!phoneResult.isValid) {
          errors.push(phoneResult.error || 'Invalid phone number');
        } else {
          setEmployee((prev) => ({ ...prev, phone: phoneResult.value }));
        }
        break;

      case 'email':
        const emailResult = validateEmail(value);
        if (!emailResult.isValid) {
          errors.push(emailResult.error || 'Invalid email address format');
        } else {
          setEmployee((prev) => ({ ...prev, email: emailResult.value }));
        }
        break;

      default:
        break;
    }

    if (errors.length > 0) {
      setErrorMessage(errors.join(', '));
    } else {
      setErrorMessage(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    try {
      await EmployeeApi.update(employee.employee_id!, employee as Employee);
      setMessage('Employee updated successfully.');
      //navigate('/main/company/employee');
    } catch (error) {
      if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('An unknown error occurred.');
      }
    }
  };

  return (
    <>
      <HorizontalMenu />
      <div className="container mx-auto max-w-7xl px-4 py-6">
        <Breadcrumbs items={breadcrumbs} />

        <h2 className="text-2xl font-semibold mb-6">Update Employee</h2>

        {message && <Alert variant="success">{message}</Alert>}
        {errorMessage && <Alert variant="error">{errorMessage}</Alert>}

        {!message && (
<div className="bg-white rounded-2xl border-2 border-muted p-6 shadow-card">
          <form onSubmit={handleSubmit} className="space-y-3 max-w-2xl">
          <div className="space-y-2">
            <div className="flex items-center gap-1">
              <Label htmlFor="first_name">First Name:</Label>
              <span className="text-red-600 font-semibold">*</span>
            </div>
            <Input
              type="text"
              id="first_name"
              name="first_name"
              value={employee.first_name || ''}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-1">
              <Label htmlFor="last_name">Last Name:</Label>
              <span className="text-red-600 font-semibold">*</span>
            </div>
            <Input
              type="text"
              id="last_name"
              name="last_name"
              value={employee.last_name || ''}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-1">
              <Label htmlFor="employee_type">Type:</Label>
              <span className="text-red-600 font-semibold">*</span>
            </div>
            <Select
              id="employee_type"
              name="employee_type"
              value={employee.employee_type || ''}
              onChange={handleInputChange}
              required
            >
              <option value="">Select Type</option>
              {employeeTypeOptions.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </Select>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-1">
              <Label htmlFor="employee_title">Title:</Label>
              <span className="text-red-600 font-semibold">*</span>
            </div>
            <Select
              id="employee_title"
              name="employee_title"
              value={employee.employee_title || ''}
              onChange={handleInputChange}
              required
            >
              <option value="">Select Title</option>
              {employeeTitleOptions.map((title) => (
                <option key={title} value={title}>
                  {title}
                </option>
              ))}
            </Select>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-1">
              <Label htmlFor="phone">Phone:</Label>
              <span className="text-red-600 font-semibold">*</span>
            </div>
            <Input
              type="text"
              id="phone"
              name="phone"
              value={employee.phone || ''}
              onChange={handleInputChange}
              onBlur={handleBlur}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-1">
              <Label htmlFor="email">Email:</Label>
              <span className="text-red-600 font-semibold">*</span>
            </div>
            <Input
              type="email"
              id="email"
              name="email"
              value={employee.email || ''}
              onChange={handleInputChange}
              onBlur={handleBlur}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="start_date">Start Date:</Label>
            <Input
              type="date"
              id="start_date"
              name="start_date"
              value={employee.start_date || ''}
              onChange={handleInputChange}
            />
          </div>

          <div className="pt-2">
            <Button type="submit" size="medium">
              <Save size={16} />
              Save Changes
            </Button>
          </div>
        </form>
        </div>
        )}
      </div>
    </>
  );
};

export default UpdateEmployeePage;